import axiosInstance from '../helpers/axios';

/** /api/ops/hubspot — the sales CRM after the move: mirror, bridge, handoff. */

export type THubspotRun = {
  at: string;
  ok?: boolean;
  error?: string;
  ms?: number;
  deals?: number;
  companies?: number;
  contacts?: number;
  owners?: number;
  pipelines?: number;
  bridge?: { auto: number; review: number };
  handoff?: { created: number; addOns: number; waitingOnBridge: number } | null;
};

export type THubspotStatus = {
  mode: 'off' | 'live';
  configured: boolean;
  missing: string[];
  every: string;
  wonStages: string[];
  handoffSince: string | null;
  counts: {
    deals: number;
    companies: number;
    contacts: number;
    bridged: number;
    unmatched: number;
    handoffs: number;
  };
  recentRuns: THubspotRun[];
};

export type TBridgeRow = {
  id: string;
  hsName: string | null;
  hsDomain: string | null;
  accountId: string | null;
  accountName: string | null;
  suggestedAccountId: string | null;
  suggestedName: string | null;
  method: string | null;
  confidence: number;
  reviewedBy: string | null;
  reviewedAt?: string | null;
  at: string;
};

export type THandoffRow = {
  dealId: string;
  kind: 'program' | 'add-on' | 'add-on-unplaced';
  programId?: string;
  caseIds?: string[];
  accountId: string;
  amount: number;
  at: string;
  by: string;
};

export type THandoffResult = {
  created: number;
  addOns: number;
  waitingOnBridge: number;
  skipped: number;
  beforeStart: number;
  ran: boolean;
  reason: string | null;
  items: {
    dealId: string;
    name?: string;
    outcome: string;
    programId?: string;
    reason?: string;
  }[];
};

export const getHubspotStatus = async () =>
  (await axiosInstance.get<THubspotStatus>('ops/hubspot/status')).data;

export const getHubspotBridge = async (status: 'unmatched' | 'all') =>
  (
    await axiosInstance.get<TBridgeRow[]>('ops/hubspot/bridge', {
      params: { status },
    })
  ).data;

export const setHubspotBridge = async (
  hsCompanyId: string,
  accountId: string | null,
) =>
  (
    await axiosInstance.post<{
      hsCompanyId: string;
      accountId: string | null;
      accountName: string | null;
      handoff: THandoffResult | null;
    }>(`ops/hubspot/bridge/${hsCompanyId}`, { accountId })
  ).data;

export const getHubspotHandoffs = async () =>
  (await axiosInstance.get<THandoffRow[]>('ops/hubspot/handoffs')).data;

export const runHubspotHandoffs = async () =>
  (await axiosInstance.post<THandoffResult>('ops/hubspot/handoff/run')).data;

export const runHubspotSync = async () =>
  (
    await axiosInstance.post<{
      ran: boolean;
      reason?: string;
      run?: THubspotRun;
    }>('ops/hubspot/sync')
  ).data;

// ----------------------------------------------- Salesforce → HubSpot import

export type TImportPlanRun = {
  kind: 'plan';
  at: string;
  by: string;
  ok?: boolean;
  error?: string;
  since?: string;
  hubspotRead?: boolean;
  pipelineFound?: boolean;
  scope?: {
    open: number;
    closedWon: number;
    closedLost: number;
    amountOpen: number;
  };
  owners?: {
    hubspot: number;
    resolved: number;
    missing: string[];
    archive?: { email: string; id: string | null } | null;
  };
  companies?: { link: number; create: number };
  contacts?: { link: number; create: number; none: number };
  flags?: Record<string, number>;
};

export type TImportStatus = {
  mode: 'live' | 'dry-run';
  readConfigured: boolean;
  writeConfigured: boolean;
  missing: string[];
  since: string;
  pipelineLabel: string;
  pipeline: {
    id: string;
    stageIds: Record<string, string>;
    unmatchedStages: string[];
  } | null;
  mirrorUrlBase: string;
  dealUrlBase?: string | null;
  counts: {
    planned: number;
    created: number;
    failed: number;
    outOfScope: number;
  };
  stages: { key: string; label: string; n: number; amount: number }[];
  companies: Record<string, number>;
  contacts: Record<string, number>;
  lastPlan: TImportPlanRun | null;
  lastApply: Record<string, unknown> | null;
};

export type TImportRow = {
  sfId: string;
  status: string;
  stageKey: string;
  open: boolean;
  properties: Record<string, string>;
  flags: string[];
  ownerEmail: string | null;
  amount: number | null;
  closeDate: string | null;
  hsDealId?: string | null;
  error?: string | null;
};

export type TOpportunityMirror = {
  opportunity: Record<string, any>;
  accountName: string | null;
  contact: { name?: string; email?: string } | null;
  owner: { name?: string; email?: string } | null;
  tasks: Record<string, any>[];
  events: Record<string, any>[];
  activityMirrored: boolean;
  importRow: TImportRow | null;
};

export const getHubspotImportStatus = async () =>
  (await axiosInstance.get<TImportStatus>('ops/hubspot/import/status')).data;

export const planHubspotImport = async () =>
  (
    await axiosInstance.post<
      TImportPlanRun & { ran: boolean; reason?: string }
    >('ops/hubspot/import/plan')
  ).data;

export const applyHubspotImport = async (limit?: number) =>
  (
    await axiosInstance.post<{
      ran: boolean;
      ok?: boolean;
      reason?: string;
      error?: string;
      wouldCreate?: number;
      created?: number;
      linked?: number;
      failed?: number;
    }>('ops/hubspot/import/apply', limit ? { limit } : {})
  ).data;

export const getHubspotImportRows = async (params: {
  status?: string;
  flag?: string;
  limit?: number;
}) =>
  (await axiosInstance.get<TImportRow[]>('ops/hubspot/import/rows', { params }))
    .data;

export const getHubspotImportOpportunity = async (id: string) =>
  (
    await axiosInstance.get<TOpportunityMirror>(
      `ops/hubspot/import/opportunity/${encodeURIComponent(id)}`,
    )
  ).data;
